<template>
  <section class="center-card noise-card">
    <div class="card-title">数据输入与降噪</div>
    <div class="chart-box">
      <div ref="chartRef" class="noise-chart"></div>
      <span class="status">隐患点多</span>
    </div>
    <p class="chart-note">随机噪声、数据漂移、局部缺失、数值突变</p>
    <div class="metric-list">
      <span v-for="item in metrics" :key="item.title">
        {{ item.title }}
        <small>{{ item.sub }}</small>
      </span>
    </div>
    <div class="summary">异常过滤、缺失修复、平滑优化</div>
  </section>
</template>

<script setup>
import * as echarts from "echarts";
import { onBeforeUnmount, onMounted, ref } from "vue";

const chartRef = ref(null);
let chartInstance = null;

const metrics = [
  { title: "空气温", sub: "湿度" },
  { title: "土壤湿度", sub: "光照强度" },
  { title: "水肥EC值", sub: "氮磷钾" },
  { title: "CO₂", sub: "浓度" },
];

const option = {
  grid: { top: 18, right: 20, bottom: 50, left: 34 },
  tooltip: {
    trigger: "axis",
    backgroundColor: "rgba(5, 38, 42, 0.92)",
    borderColor: "rgba(84, 255, 240, 0.5)",
    textStyle: { color: "#fff", fontSize: 22 },
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: ["07-01", "07-02", "07-03", "07-04", "07-05", "07-06", "07-07", "07-08", "07-09", "07-10"],
    axisTick: { show: false },
    axisLine: { lineStyle: { color: "rgba(130, 255, 246, 0.28)" } },
    axisLabel: {
      color: "rgba(230, 255, 253, 0.72)",
      fontSize: 22,
      fontWeight: "bold",
      margin: 12,
    },
    splitLine: {
      show: true,
      lineStyle: { color: "rgba(135, 255, 245, 0.12)", type: "dashed" },
    },
  },
  yAxis: {
    type: "value",
    min: 0,
    max: 100,
    interval: 20,
    axisTick: { show: false },
    axisLine: { show: false },
    axisLabel: {
      color: "rgba(230, 255, 253, 0.72)",
      fontSize: 22,
      fontWeight: "bold",
    },
    splitLine: {
      lineStyle: { color: "rgba(135, 255, 245, 0.15)", type: "dashed" },
    },
  },
  series: [
    {
      type: "line",
      smooth: true,
      symbol: "none",
      data: [18, 22, 19, 64, 31, 44, 27, 25, 22, 20],
      lineStyle: {
        width: 3,
        color: "#72fff3",
        shadowColor: "rgba(114, 255, 243, 0.75)",
        shadowBlur: 10,
      },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgba(83, 255, 239, 0.38)" },
          { offset: 0.58, color: "rgba(38, 199, 183, 0.18)" },
          { offset: 1, color: "rgba(38, 199, 183, 0.02)" },
        ]),
      },
      markLine: {
        symbol: "none",
        silent: true,
        label: { show: false },
        lineStyle: { color: "rgba(101, 255, 242, 0.36)", type: "solid", width: 1 },
        data: [{ xAxis: "07-04" }, { xAxis: "07-06" }],
      },
    },
  ],
};

function resizeChart() {
  chartInstance && chartInstance.resize();
}

onMounted(() => {
  chartInstance = echarts.init(chartRef.value);
  chartInstance.setOption(option);
  window.addEventListener("resize", resizeChart);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeChart);
  chartInstance && chartInstance.dispose();
});
</script>

<style lang="less" scoped>
@import "./centerPanelShared.less";

.noise-card {
  padding: 70px 28px 42px;
}

.chart-box {
  position: relative;
  height: 418px;
  margin-top: 80px;
}

.noise-chart {
  width: 100%;
  height: 100%;
}

.status {
  position: absolute;
  top: 7px;
  right: 5px;
  padding: 9px 18px;
  color: #ffffff;
  font-size: 15px;
  font-weight: bold;
  line-height: 18px;
  border-radius: 3px;
  background: linear-gradient(180deg, rgba(55, 222, 232, 0.96), rgba(18, 171, 184, 0.96));
  box-shadow: 0 0 12px rgba(46, 221, 232, 0.42);
}

.chart-note {
  margin: 26px 0 50px;
  color: #ffffff;
  font-size: 25px;
  font-weight: 900;
  line-height: 32px;
  text-align: center;
  white-space: nowrap;
  text-shadow: 0 0 9px rgba(255, 255, 255, 0.28);
}

.metric-list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.metric-list span {
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  box-sizing: border-box;
  color: #eafffd;
  font-size: 24px;
  font-weight: 900;
  line-height: 40px;
  text-align: center;
  border: 1px solid rgba(82, 255, 241, 0.56);
  border-radius: 5px;
  background:
    radial-gradient(circle at 50% 0, rgba(93, 255, 242, 0.22), transparent 64%),
    linear-gradient(180deg, rgba(20, 151, 148, 0.83), rgba(7, 88, 91, 0.9));
  box-shadow:
    inset 0 0 22px rgba(66, 255, 239, 0.2),
    0 0 10px rgba(37, 223, 211, 0.14);
}

.metric-list small {
  display: block;
  color: inherit;
  font-size: 24px;
  font-weight: 900;
  line-height: 27px;
}

.summary {
  margin-top: 50px;
  position: relative;
  padding: 20px 12px 22px;
  color: #ffffff;
  font-size: 31px;
  font-weight: 900;
  line-height: 38px;
  text-align: center;
  border-radius: 4px;
  background:
    linear-gradient(90deg, rgba(25, 126, 130, 0.28), rgba(44, 209, 195, 0.22), rgba(25, 126, 130, 0.28)),
    linear-gradient(180deg, rgba(10, 90, 91, 0.48), rgba(2, 45, 52, 0.54));
  text-shadow: 0 0 12px rgba(255, 255, 255, 0.28);
}

.summary::after {
  content: "";
  position: absolute;
  left: 16%;
  right: 16%;
  bottom: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(99, 255, 245, 0.9), transparent);
  box-shadow: 0 0 10px rgba(99, 255, 245, 0.72);
}
</style>
